import { ArrowRight, Download } from "lucide-react";
import { jsPDF } from "jspdf";
import { scrollToSection } from "@/components/landing/Navbar";
import { Reveal } from "@/components/landing/Reveal";
import { Star } from "@/components/landing/Logo";
import { useLang } from "@/i18n/LanguageContext";

const IMG_STORE = "https://static.prod-images.emergentagent.com/jobs/78bcda24-ecc7-4097-99a8-74d5dca5b1b3/images/c8585d1701c3bb33ee950a5fed68e15af7e26578f85cba11c5b81d264c599954.jpeg";
const IMG_FITTING = "https://static.prod-images.emergentagent.com/jobs/78bcda24-ecc7-4097-99a8-74d5dca5b1b3/images/9dad48963b9b8378c3b9a4f70ad564fe49df5d339fb2428592191a764e6b099f.jpeg";

const clean = (t) =>
  String(t)
    .replace(/″/g, '"')
    .replace(/[„“”]/g, '"')
    .replace(/[’‘]/g, "'")
    .replace(/–/g, "-")
    .replace(/…/g, "...")
    .replace(/✦/g, "");

const downloadOnepager = (c) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = 595.28;
  const H = 841.89;
  const M = 46;
  const maxW = W - M * 2;
  doc.setFillColor(252, 250, 248);
  doc.rect(0, 0, W, H, "F");
  let y = 52;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(198, 156, 109);
  doc.text("M I R A   M I R R O R", M, y);
  doc.setFontSize(8);
  doc.setTextColor(138, 127, 121);
  doc.text(clean(c.kicker).toUpperCase() + "  ·  " + clean(c.badge).toUpperCase(), W - M, y, { align: "right" });
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(1);
  doc.line(M, y + 10, W - M, y + 10);
  y += 36;

  doc.setFont("times", "bold");
  doc.setFontSize(19);
  doc.setTextColor(44, 39, 37);
  const title = doc.splitTextToSize(clean(c.title), maxW);
  doc.text(title, M, y);
  y += title.length * 21 + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 82, 78);
  const intro = doc.splitTextToSize(clean(c.intro), maxW);
  doc.text(intro, M, y);
  y += intro.length * 11 + 6;

  doc.setFont("helvetica", "oblique");
  doc.setFontSize(7);
  doc.setTextColor(138, 127, 121);
  const disc = doc.splitTextToSize(clean(c.disclaimer), maxW);
  doc.text(disc, M, y);
  y += disc.length * 9 + 14;

  c.sections.forEach((s) => {
    doc.setFont("times", "bold");
    doc.setFontSize(12.5);
    doc.setTextColor(44, 39, 37);
    doc.text(clean(s.h), M, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(90, 82, 78);
    const lines = doc.splitTextToSize(clean(s.p), maxW);
    doc.text(lines, M, y);
    y += lines.length * 10.5 + 11;
  });

  doc.setFont("times", "bold");
  doc.setFontSize(12.5);
  doc.setTextColor(44, 39, 37);
  doc.text(clean(c.benefitsTitle), M, y);
  y += 15;
  c.benefits.forEach((b) => {
    doc.setFillColor(212, 175, 55);
    doc.circle(M + 3, y - 2.5, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(44, 39, 37);
    doc.text(clean(b.title), M + 11, y);
    y += 10.5;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(90, 82, 78);
    const lines = doc.splitTextToSize(clean(b.text), maxW - 11);
    doc.text(lines, M + 11, y);
    y += lines.length * 10 + 7;
  });

  y += 6;
  doc.setFont("times", "bolditalic");
  doc.setFontSize(11);
  doc.setTextColor(44, 39, 37);
  const quote = doc.splitTextToSize('"' + clean(c.quote) + '"', maxW - 40);
  doc.text(quote, W / 2, y, { align: "center" });
  y += quote.length * 13 + 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(198, 156, 109);
  doc.text(clean(c.quoteName) + " - " + clean(c.quoteRole), W / 2, y, { align: "center" });

  doc.setDrawColor(234, 225, 219);
  doc.line(M, H - 54, W - M, H - 54);
  doc.setFontSize(7.5);
  doc.setTextColor(138, 127, 121);
  doc.text("MYSYMP AG  ·  Studenstrasse 14B, CH-6207 Nottwil  ·  info@mysymp.ch  ·  +41 79 471 06 25  ·  mira-mirror.ai", W / 2, H - 38, { align: "center" });

  doc.save("mira-mirror-case-study-onepager.pdf");
};

export default function CaseStudySection() {
  const { t, lang } = useLang();
  const c = t.caseStudy;

  const goToForm = () => scrollToSection("boutiquen");

  return (
    <section id="casestudy" style={{ background: "var(--surface)", color: "var(--on-surface)" }}>
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-24" data-testid="casestudy-section">
        <Reveal>
          <div className="flex flex-wrap items-center gap-3">
            <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
              <Star size={11} /> {c.kicker}
            </p>
            <span className="rounded-full border px-3 py-1 text-[10px] font-bold uppercase" style={{ borderColor: "var(--gold)", color: "var(--camel)", letterSpacing: "0.1em" }} data-testid="casestudy-badge">
              {c.badge}
            </span>
          </div>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight" data-testid="casestudy-title">
            {c.title}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>{c.intro}</p>
          <p className="mt-4 rounded-2xl border px-5 py-3.5 text-xs leading-relaxed" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)", color: "var(--muted-warm)" }} data-testid="casestudy-disclaimer">
            ✦ {c.disclaimer}
          </p>
          <button
            onClick={() => downloadOnepager(c)}
            data-testid="casestudy-pdf-btn"
            className="pill-btn mt-6 inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-medium transition-colors duration-300 hover:text-[var(--camel)]"
            style={{ borderColor: "var(--gold)", color: "var(--on-surface)", background: "var(--surface)" }}
          >
            <Download size={15} style={{ color: "var(--camel)" }} /> {c.download}
          </button>
        </Reveal>

        <Reveal className="mt-12">
          <div className="overflow-hidden rounded-3xl border" style={{ borderColor: "var(--line)" }}>
            <img src={IMG_STORE} alt={c.title} className="h-64 sm:h-96 w-full object-cover" loading="lazy" />
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <div className="overflow-hidden rounded-3xl border-2 shadow-xl" style={{ borderColor: "var(--line)", background: "#000" }} data-testid="casestudy-video">
            <video
              src={lang === "de" ? "/videos/schaufenster-emo-de.mp4" : "/videos/schaufenster-emo-en.mp4"}
              poster="/videos/schaufenster-emo-poster.webp"
              controls
              playsInline
              preload="metadata"
              className="aspect-video w-full object-cover"
              onError={(e) => { if (e.currentTarget.src.includes("-en")) e.currentTarget.src = "/videos/schaufenster-emo-de.mp4"; }}
            />
          </div>
          {t.windowLooks && (
            <p className="mt-3 text-center text-xs" style={{ color: "var(--muted-warm)" }}>{t.windowLooks.videoLabel}</p>
          )}
        </Reveal>

        <div className="mt-14 space-y-12">
          {c.sections.map((s, i) => (
            <Reveal key={i} data-testid={`casestudy-section-${i + 1}`}>
              <h2 className="font-serif-display text-3xl font-semibold">{s.h}</h2>
              <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{s.p}</p>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14">
          <div className="overflow-hidden rounded-3xl border" style={{ borderColor: "var(--line)" }}>
            <img src={IMG_FITTING} alt={c.benefitsTitle} className="h-64 sm:h-96 w-full object-cover" loading="lazy" />
          </div>
        </Reveal>

        <Reveal className="mt-14">
          <h2 className="font-serif-display text-3xl font-semibold" data-testid="casestudy-benefits-title">{c.benefitsTitle}</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {c.benefits.map((b, i) => (
            <Reveal
              key={i}
              delay={i * 0.08}
              className="rounded-3xl border p-7"
              style={{ background: "var(--surface-secondary)", borderColor: "var(--line)" }}
              data-testid={`casestudy-benefit-${i + 1}`}
            >
              <span style={{ color: "var(--gold)" }}>✦</span>
              <h3 className="mt-3 font-serif-display text-xl font-semibold">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{b.text}</p>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16">
          <blockquote className="rounded-3xl border-2 p-9 sm:p-12 text-center" style={{ borderColor: "var(--gold)", background: "var(--surface-secondary)" }} data-testid="casestudy-quote">
            <p className="font-serif-display text-2xl sm:text-3xl font-semibold leading-snug">„{c.quote}“</p>
            <p className="mt-5 text-sm font-medium" style={{ color: "var(--camel)" }}>{c.quoteName}</p>
            <p className="text-xs" style={{ color: "var(--muted-warm)" }}>{c.quoteRole}</p>
          </blockquote>
        </Reveal>

        <Reveal className="mt-16 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold">{c.ctaTitle}</h2>
          <button
            onClick={goToForm}
            data-testid="casestudy-cta-btn"
            className="pill-btn mt-7 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-medium text-white"
            style={{ background: "var(--camel)" }}
          >
            {c.ctaBtn} <ArrowRight size={15} />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
